import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";

// The mascot, leaning toward the pointer.
//
// Not eye-tracking: the drawing has its eyes squeezed shut, mid-laugh, and there
// is no separate eye layer to move. Leaning the whole character is what the asset
// actually allows, and it suits a fox already throwing horns — it reads as the
// thing turning to look at you, not as a puppet.
//
// The listener is on the window rather than the image, so the fox notices a
// pointer crossing the page instead of waiting to be hovered. That is the whole
// difference between a decoration and something that feels alive.

// How far from the mascot's centre the pointer still has an effect. Past this
// the lean is at full tilt, so crossing the far side of the page does not keep
// pushing it further.
const REACH = 600;

// Full tilt. Small on purpose: this is a character noticing you, and a drawing
// that swings hard reads as a toy.
const MAX_ROTATION = 5;
const MAX_SHIFT_X = 12;
const MAX_SHIFT_Y = 7;

// Heavy enough to overshoot slightly and settle, which is what makes it read as
// weight rather than as a value being assigned.
const SPRING = { stiffness: 120, damping: 18, mass: 0.6 };

const clamp = (value) => Math.max(-1, Math.min(1, value));

export const Mascot = ({ className = "", width = 870, height = 900 }) => {
    const reduceMotion = useReducedMotion();
    const ref = useRef(null);

    const pointerX = useMotionValue(0);
    const pointerY = useMotionValue(0);

    const springX = useSpring(pointerX, SPRING);
    const springY = useSpring(pointerY, SPRING);

    const rotate = useTransform(springX, [-1, 1], [-MAX_ROTATION, MAX_ROTATION]);
    const x = useTransform(springX, [-1, 1], [-MAX_SHIFT_X, MAX_SHIFT_X]);
    const y = useTransform(springY, [-1, 1], [-MAX_SHIFT_Y, MAX_SHIFT_Y]);

    useEffect(() => {
        if (reduceMotion) return undefined;

        const onPointerMove = (event) => {
            // Touch drags fire pointermove too, and a fox that lurches while the
            // page is being scrolled by thumb is noise. Mice only.
            if (event.pointerType !== "mouse") return;

            const el = ref.current;
            if (!el) return;

            const box = el.getBoundingClientRect();
            pointerX.set(clamp((event.clientX - (box.left + box.width / 2)) / REACH));
            pointerY.set(clamp((event.clientY - (box.top + box.height / 2)) / REACH));
        };

        // The pointer leaving the window strands the fox mid-lean, so send it home.
        const onPointerLeave = () => {
            pointerX.set(0);
            pointerY.set(0);
        };

        window.addEventListener("pointermove", onPointerMove, { passive: true });
        document.addEventListener("pointerleave", onPointerLeave);

        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerleave", onPointerLeave);
        };
    }, [reduceMotion, pointerX, pointerY]);

    return (
        <motion.img
            ref={ref}
            src="/images/brand/mascote.webp"
            alt=""
            aria-hidden
            width={width}
            height={height}
            className={className}
            style={reduceMotion ? undefined : { rotate, x, y }}
        />
    );
};
